import React, { useState, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import type {
  QualityMetricsVisualizationProps,
  QualityMetricsData,
  DateRange
} from '../../types/advanced_content_quality_ui';

// Chart configuration
const CHART_COLORS = {
  clarity: '#3B82F6',
  coherence: '#10B981', 
  impact: '#8B5CF6',
  overall: '#F59E0B'
};

// Time range options
const TIME_RANGES = [
  { label: '24 Hours', value: '24h' },
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' },
  { label: '90 Days', value: '90d' }
];

export const QualityMetricsVisualization: React.FC<QualityMetricsVisualizationProps> = ({
  metrics,
  showTrends = true,
  timeRange,
  comparison = 'none',
  interactive = true,
  onMetricClick
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'breakdown' | 'trends' | 'comparison'>('breakdown');

  // Calculate metric percentages for visualization
  const metricData = useMemo(() => [
    {
      name: 'Clarity',
      score: metrics.clarityScoring.score,
      breakdown: metrics.clarityScoring.breakdown,
      color: CHART_COLORS.clarity,
      icon: '🎯',
      trend: metrics.clarityScoring.trend
    },
    {
      name: 'Coherence', 
      score: metrics.coherenceScoring.score,
      breakdown: metrics.coherenceScoring.breakdown,
      color: CHART_COLORS.coherence,
      icon: '🔗',
      trend: metrics.coherenceScoring.trend
    },
    {
      name: 'Impact',
      score: metrics.impactScoring.score,
      breakdown: metrics.impactScoring.breakdown,
      color: CHART_COLORS.impact,
      icon: '💥',
      trend: metrics.impactScoring.trend
    }
  ], [metrics]);

  // Get overall quality grade styling
  const getGradeStyle = (grade: string) => {
    const baseClasses = 'text-2xl font-bold px-4 py-2 rounded-lg';
    switch (grade) {
      case 'A+':
      case 'A':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'B+':
      case 'B':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'C+':
      case 'C':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'D':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      case 'F':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Handle metric click
  const handleMetricClick = (metricName: string) => {
    if (!interactive) return;
    
    setSelectedMetric(selectedMetric === metricName ? null : metricName);
    onMetricClick?.(metricName);
  };

  // Render circular progress indicator
  const renderCircularProgress = (score: number, color: string, size: number = 120) => {
    const circumference = 2 * Math.PI * 45;
    const strokeDasharray = `${(score / 100) * circumference} ${circumference}`;
    
    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          className="transform -rotate-90"
          width={size}
          height={size}
          viewBox="0 0 100 100"
        >
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            className="transition-all duration-500 ease-out"
          />
        </svg>
        {/* Score text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-gray-900">{score}%</span>
        </div>
      </div>
    );
  };

  // Render metric breakdown
  const renderMetricBreakdown = (breakdown: Record<string, number>, color: string) => {
    return (
      <div className="space-y-2">
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-sm text-gray-600 capitalize">
              {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
            </span>
            <div className="flex items-center space-x-2">
              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${value}%`,
                    backgroundColor: color
                  }}
                />
              </div>
              <span className="text-sm font-medium w-8 text-right">{value}%</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render trend sparkline
  const renderTrendSparkline = (trend: Array<{ timestamp: Date; value: number }>, color: string) => {
    if (!trend.length) return null;

    const maxValue = Math.max(...trend.map(t => t.value));
    const minValue = Math.min(...trend.map(t => t.value));
    const range = maxValue - minValue || 1;

    const points = trend.map((point, index) => {
      const x = (index / (trend.length - 1)) * 100;
      const y = 100 - ((point.value - minValue) / range) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="mt-2">
        <svg width="100%" height="40" className="overflow-visible">
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth="2"
            className="opacity-70"
          />
          {/* Trend dots */}
          {trend.map((point, index) => {
            const x = (index / (trend.length - 1)) * 100;
            const y = 100 - ((point.value - minValue) / range) * 100;
            return (
              <circle
                key={index}
                cx={`${x}%`}
                cy={`${y}%`}
                r="2"
                fill={color}
                className="opacity-80"
              />
            );
          })}
        </svg>
      </div>
    );
  };

  // Render comparison view
  const renderComparisonView = () => {
    if (comparison === 'none') return null;

    const benchmarkData = metrics.overallQualityScore.benchmarkComparison;
    
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Benchmark Comparison</h3>
        <div className="space-y-4">
          {benchmarkData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm text-gray-600">Your Score</div>
                <div className="text-2xl font-bold text-blue-600">
                  {metrics.overallQualityScore.compositeScore}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Industry Average</div>
                <div className="text-2xl font-bold text-gray-600">75%</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Top Performers</div>
                <div className="text-2xl font-bold text-green-600">92%</div>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="quality-metrics-visualization space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Quality Metrics</h2>
        
        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {(['breakdown', 'trends', 'comparison'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === mode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>

          {/* Time Range Selector */}
          {showTrends && (
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
            >
              {TIME_RANGES.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Overall Quality Score */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Overall Quality Score</h3>
            <p className="text-gray-600 mt-1">Composite assessment across all metrics</p>
          </div>
          <div className="flex items-center space-x-6">
            {renderCircularProgress(metrics.overallQualityScore.compositeScore, CHART_COLORS.overall, 100)}
            <div className={getGradeStyle(metrics.overallQualityScore.qualityGrade)}>
              {metrics.overallQualityScore.qualityGrade}
            </div>
          </div>
        </div>
      </Card>

      {/* Main Content based on view mode */}
      {viewMode === 'breakdown' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {metricData.map((metric) => (
            <Card
              key={metric.name}
              className={`p-6 cursor-pointer transition-all duration-200 ${
                interactive ? 'hover:shadow-lg hover:-translate-y-1' : ''
              } ${selectedMetric === metric.name ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => handleMetricClick(metric.name)}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{metric.icon}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{metric.name}</h3>
                    <p className="text-sm text-gray-600">Quality Assessment</p>
                  </div>
                </div>
                {renderCircularProgress(metric.score, metric.color, 80)}
              </div>

              {/* Metric Breakdown */}
              {renderMetricBreakdown(metric.breakdown, metric.color)}

              {/* Trend Sparkline */}
              {showTrends && metric.trend.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Trend</span>
                    <span className="text-xs text-gray-500">{selectedTimeRange}</span>
                  </div>
                  {renderTrendSparkline(metric.trend, metric.color)}
                </div>
              )}

              {/* Insights */}
              {metric.name === 'Clarity' && metrics.clarityScoring.insights.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Key Insight:</span>
                    <p className="mt-1">{metrics.clarityScoring.insights[0]}</p>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {viewMode === 'trends' && showTrends && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Trends</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {metricData.map((metric) => (
              <div key={metric.name} className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{metric.icon}</span>
                  <span className="font-medium text-gray-900">{metric.name}</span>
                  <span className="text-sm text-gray-600">({metric.score}%)</span>
                </div>
                {metric.trend.length > 0 ? (
                  renderTrendSparkline(metric.trend, metric.color)
                ) : (
                  <div className="text-sm text-gray-500 italic">No trend data available</div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {viewMode === 'comparison' && renderComparisonView()}

      {/* Real-time Assessment Status */}
      {metrics.realTimeAssessment.isAssessing && (
        <Card className="p-4 border-blue-200 bg-blue-50">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="text-blue-800 font-medium">Real-time Quality Assessment in Progress</div>
              <div className="text-blue-600 text-sm mt-1">
                Progress: {metrics.realTimeAssessment.assessmentProgress}%
                {metrics.realTimeAssessment.estimatedCompletion && (
                  <span className="ml-2">
                    • ETA: {metrics.realTimeAssessment.estimatedCompletion.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
            <div className="w-16 h-2 bg-blue-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${metrics.realTimeAssessment.assessmentProgress}%` }}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default QualityMetricsVisualization;
